// Reusable CSV/XLSX import dialog. Parses the file in the browser, previews the rows,
// posts them to an import endpoint ({rows:[...]}) and shows the result. A "Download
// template" link gives the user the expected columns.
import { useRef, useState } from 'react';
import { parseImportFile, downloadCsvTemplate } from '../lib/importFile';
import { api } from '../lib/api';

type Props = {
  title: string;
  endpoint: string;                 // e.g. '/bead/awards/import'
  templateHeaders: string[];        // columns shown in the downloadable template
  sampleRow?: Record<string, string>;
  help?: string;
  onClose: () => void;
  onDone: () => void;               // called after a successful import (to refetch)
};

export default function ImportModal({ title, endpoint, templateHeaders, sampleRow, help, onClose, onDone }: Props) {
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPick = async (f?: File | null) => {
    if (!f) return;
    setErr(null); setResult(null);
    try {
      const p = await parseImportFile(f);
      if (!p.rows.length) { setErr('That file has no data rows.'); return; }
      setRows(p.rows); setHeaders(p.headers); setFileName(f.name);
    } catch (e: any) { setErr('Could not read the file: ' + (e?.message ?? e)); }
  };

  const doImport = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(endpoint, { rows });
      setResult(r);
      if ((r?.inserted ?? 0) > 0) onDone();
    } catch (e: any) { setErr(e?.message ?? 'Import failed'); }
    finally { setBusy(false); }
  };

  const inp: React.CSSProperties = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13 };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '6vh 16px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: 720, maxWidth: '100%', maxHeight: '86vh', overflow: 'auto', boxShadow: '0 20px 50px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <b style={{ fontSize: 16 }}>{title}</b>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
            Upload a <b>CSV</b> or <b>Excel (.xlsx)</b> file. {help}
            {' '}<a onClick={(e) => { e.preventDefault(); downloadCsvTemplate((endpoint.split('/').pop() || 'import') + '-template.csv', templateHeaders, sampleRow); }}
              href="#" style={{ color: '#2563eb', textDecoration: 'underline' }}>Download a template</a> to see the columns.
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv" onChange={(e) => onPick(e.target.files?.[0])} style={inp} />
            {fileName && <span style={{ fontSize: 12, color: '#64748b' }}>{rows.length} row{rows.length === 1 ? '' : 's'} · {esc(fileName)}</span>}
          </div>

          {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 10px', borderRadius: 4, fontSize: 13, marginBottom: 12 }}>{err}</div>}

          {rows.length > 0 && !result && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'auto', maxHeight: 240, marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: '#f8fafc' }}>{headers.map((h) => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{esc(h)}</th>)}</tr></thead>
                <tbody>{rows.slice(0, 8).map((r, i) => <tr key={i}>{headers.map((h) => <td key={h} style={{ padding: '5px 8px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{esc(String(r[h] ?? ''))}</td>)}</tr>)}</tbody>
              </table>
              {rows.length > 8 && <div style={{ padding: '5px 8px', fontSize: 11, color: '#94a3b8' }}>+ {rows.length - 8} more…</div>}
            </div>
          )}

          {result && (
            <div style={{ background: (result.inserted ?? 0) > 0 ? '#f0fdf4' : '#fffbeb', border: '1px solid ' + ((result.inserted ?? 0) > 0 ? '#bbf7d0' : '#fde68a'), borderRadius: 6, padding: 12, fontSize: 13, marginBottom: 12 }}>
              <b>Imported {result.inserted ?? 0}</b>{typeof result.updated === 'number' ? ` (${result.added} new, ${result.updated} updated)` : ''}
              {result.skipped ? ` · skipped ${result.skipped}` : ''}.
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#92400e' }}>
                  {result.errors.slice(0, 10).map((e: any, i: number) => <li key={i}>Row {e.row}{e.code ? ` (${esc(e.code)})` : ''}: {esc(String(e.error))}</li>)}
                </ul>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} className="btn" style={{ padding: '7px 14px', border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>{result ? 'Close' : 'Cancel'}</button>
            {!result && <button onClick={doImport} disabled={busy || rows.length === 0} className="btn btn--primary" style={{ padding: '7px 14px', border: 'none', borderRadius: 4, background: rows.length ? '#2563eb' : '#94a3b8', color: '#fff', cursor: rows.length ? 'pointer' : 'default' }}>{busy ? 'Importing…' : `Import ${rows.length || ''} row${rows.length === 1 ? '' : 's'}`}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function esc(s: string) { return s; }
